import { useState, useEffect } from 'react';
import { dashboardApi } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { MapPin, Scissors, SprayCan, Tractor, Locate, Layers, X, FileText, Activity } from 'lucide-react';
import Map, { Source, Layer, NavigationControl, Marker, Popup } from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';

const MAP_STYLE = {
  version: 8,
  sources: { 'esri-satellite': { type: 'raster', tiles: ['https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'], tileSize: 256, attribution: '&copy; Esri' } },
  layers: [{ id: 'esri-satellite', type: 'raster', source: 'esri-satellite', minzoom: 0, maxzoom: 19 }]
};

const FARM_CENTER = { longitude: 153.137, latitude: -27.943, zoom: 15 };
const CUT_COLORS = ['#C026D3', '#9333EA', '#7C3AED', '#6366F1', '#3B82F6', '#06B6D4', '#10B981', '#F59E0B'];
const ACTIVITY_ICONS = { mowing: '🟢', spraying: '🔵', fertilizing: '🟡', watering: '💧', harvesting: '🟠', 'chook poo': '🟤', 'soil prep': '⚫' };

export function FarmMapPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedField, setSelectedField] = useState(null);
  const [showCutAreas, setShowCutAreas] = useState(true);
  const [showActivities, setShowActivities] = useState(true);
  const [showVehicles, setShowVehicles] = useState(true);
  const [viewState, setViewState] = useState({ longitude: FARM_CENTER.longitude, latitude: FARM_CENTER.latitude, zoom: FARM_CENTER.zoom });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const res = await dashboardApi.getFarmOverview();
      setData(res.data);
      // Auto-center on fields
      const allPts = [];
      res.data.fields.forEach(f => { if (f.coordinates?.length > 0) allPts.push(...f.coordinates); });
      if (allPts.length > 0) {
        const lngs = allPts.map(p => p[0]); const lats = allPts.map(p => p[1]);
        setViewState({ longitude: (Math.min(...lngs) + Math.max(...lngs)) / 2, latitude: (Math.min(...lats) + Math.max(...lats)) / 2, zoom: 16 });
      }
    } catch { toast.error('Failed to load farm data'); } finally { setLoading(false); }
  };

  const goToMyLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setViewState(prev => ({ ...prev, longitude: pos.coords.longitude, latitude: pos.coords.latitude, zoom: 18 })),
      () => {}, { enableHighAccuracy: true }
    );
  };

  const getFieldCenter = (field) => {
    if (!field.coordinates?.length) return null;
    const lngs = field.coordinates.map(p => p[0]); const lats = field.coordinates.map(p => p[1]);
    return [(Math.min(...lngs) + Math.max(...lngs)) / 2, (Math.min(...lats) + Math.max(...lats)) / 2];
  };

  if (loading) return <div className="animate-pulse h-[calc(100vh-120px)] bg-muted rounded-md" data-testid="farm-map-loading" />;

  return (
    <div className="space-y-4 animate-fade-in" data-testid="farm-map-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">Farm Map</h1>
          <p className="text-muted-foreground">Overview of all fields, cut areas, activities & machinery</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" variant={showCutAreas ? 'default' : 'outline'} onClick={() => setShowCutAreas(!showCutAreas)}
            className={showCutAreas ? 'bg-purple-600 hover:bg-purple-700 text-white' : ''} data-testid="toggle-cuts-btn">
            <Scissors className="h-3 w-3 mr-1" /> Cuts
          </Button>
          <Button size="sm" variant={showActivities ? 'default' : 'outline'} onClick={() => setShowActivities(!showActivities)}
            className={showActivities ? 'bg-blue-600 hover:bg-blue-700 text-white' : ''} data-testid="toggle-activities-btn">
            <Activity className="h-3 w-3 mr-1" /> Activities
          </Button>
          <Button size="sm" variant={showVehicles ? 'default' : 'outline'} onClick={() => setShowVehicles(!showVehicles)}
            className={showVehicles ? 'bg-amber-600 hover:bg-amber-700 text-white' : ''} data-testid="toggle-vehicles-btn">
            <Tractor className="h-3 w-3 mr-1" /> Vehicles
          </Button>
          <Button size="sm" variant="ghost" onClick={goToMyLocation} data-testid="farm-map-location-btn">
            <Locate className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="relative">
        <div className="h-[calc(100vh-220px)] min-h-[400px] rounded-lg overflow-hidden border shadow-lg" data-testid="farm-overview-map">
          <Map {...viewState} onMove={(e) => setViewState(e.viewState)} mapStyle={MAP_STYLE} style={{ width: '100%', height: '100%' }}>
            <NavigationControl position="top-right" />

            {/* Field boundaries */}
            {data?.fields?.filter(f => f.coordinates?.length >= 3).map((field) => (
              <Source key={`field-${field.id}`} id={`field-${field.id}`} type="geojson"
                data={{ type: 'Feature', properties: { id: field.id }, geometry: { type: 'Polygon', coordinates: [[...field.coordinates, field.coordinates[0]]] } }}>
                <Layer id={`field-fill-${field.id}`} type="fill" paint={{ 'fill-color': '#1B4332', 'fill-opacity': selectedField?.id === field.id ? 0.25 : 0.1 }} />
                <Layer id={`field-line-${field.id}`} type="line" paint={{ 'line-color': '#EF4444', 'line-width': selectedField?.id === field.id ? 3 : 2, 'line-dasharray': [4, 3] }} />
              </Source>
            ))}

            {/* Cut area polygons */}
            {showCutAreas && data?.fields?.map((field) =>
              field.cut_polygons?.filter(cp => cp.points?.length >= 3).map((cp, cpIdx) => (
                <Source key={`cut-${field.id}-${cpIdx}`} id={`cut-${field.id}-${cpIdx}`} type="geojson"
                  data={{ type: 'Feature', geometry: { type: 'Polygon', coordinates: [[...cp.points, cp.points[0]]] } }}>
                  <Layer id={`cut-fill-${field.id}-${cpIdx}`} type="fill" paint={{ 'fill-color': CUT_COLORS[cpIdx % CUT_COLORS.length], 'fill-opacity': 0.4 }} />
                  <Layer id={`cut-line-${field.id}-${cpIdx}`} type="line" paint={{ 'line-color': CUT_COLORS[cpIdx % CUT_COLORS.length], 'line-width': 1.5 }} />
                </Source>
              ))
            )}

            {/* Activity markers */}
            {showActivities && data?.fields?.map((field) => {
              const center = getFieldCenter(field);
              return field.recent_activities?.slice(0, 3).map((act, i) => {
                const coords = act.area_coordinates?.length > 0 ? act.area_coordinates[0] : center;
                if (!coords) return null;
                const lng = Array.isArray(coords) ? coords[0] : center?.[0];
                const lat = Array.isArray(coords) ? coords[1] : center?.[1];
                if (!lng || !lat) return null;
                return (
                  <Marker key={`act-${act.id}`} longitude={lng} latitude={lat} anchor="center">
                    <div className="w-6 h-6 rounded-full bg-blue-500 border-2 border-white shadow-lg flex items-center justify-center text-[10px]" title={`${act.type} - ${act.date}`}>
                      {ACTIVITY_ICONS[act.type] || <SprayCan className="h-3 w-3 text-white" />}
                    </div>
                  </Marker>
                );
              });
            })}

            {/* Field name labels */}
            {data?.fields?.filter(f => f.coordinates?.length >= 3).map((field) => {
              const center = getFieldCenter(field);
              if (!center) return null;
              return (
                <Marker key={`label-${field.id}`} longitude={center[0]} latitude={center[1]} anchor="center"
                  onClick={(e) => { e.originalEvent.stopPropagation(); setSelectedField(field); }}>
                  <div className="cursor-pointer px-2 py-1 rounded-md bg-black/70 text-white text-xs font-semibold shadow-lg border border-white/20 whitespace-nowrap hover:bg-black/90 transition-colors"
                    data-testid={`field-label-${field.id}`}>
                    {field.name}
                  </div>
                </Marker>
              );
            })}

            {/* Selected field popup */}
            {selectedField && getFieldCenter(selectedField) && (
              <Popup longitude={getFieldCenter(selectedField)[0]} latitude={getFieldCenter(selectedField)[1]}
                anchor="bottom" onClose={() => setSelectedField(null)} closeButton={false} maxWidth="320px">
                <div className="p-3 space-y-2 min-w-[260px]" data-testid="field-popup">
                  <div className="flex items-center justify-between">
                    <h3 className="font-bold text-base">{selectedField.name}</h3>
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => setSelectedField(null)}><X className="h-4 w-4" /></Button>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-gray-50 rounded p-1.5">
                      <p className="text-muted-foreground">Area</p>
                      <p className="font-semibold">{selectedField.area_hectares || '—'} ha</p>
                    </div>
                    <div className="bg-purple-50 rounded p-1.5">
                      <p className="text-muted-foreground">Harvested</p>
                      <p className="font-semibold text-purple-700">{selectedField.total_harvested_sqm?.toLocaleString() || 0} sqm</p>
                    </div>
                    <div className="bg-green-50 rounded p-1.5">
                      <p className="text-muted-foreground">Last Cut</p>
                      <p className="font-semibold text-green-700">{selectedField.last_cut_date || 'Never'}</p>
                    </div>
                    <div className="bg-blue-50 rounded p-1.5">
                      <p className="text-muted-foreground">Activities</p>
                      <p className="font-semibold text-blue-700">{selectedField.active_activities || 0}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground pt-1 border-t">
                    <FileText className="h-3 w-3" />
                    <span>{selectedField.field_notes_count || 0} field notes</span>
                    {selectedField.cut_polygons?.length > 0 && (
                      <><span className="mx-1">|</span><MapPin className="h-3 w-3" /><span>{selectedField.cut_polygons.length} cut areas</span></>
                    )}
                  </div>
                </div>
              </Popup>
            )}
          </Map>
        </div>

        {/* Legend */}
        <div className="absolute bottom-4 left-4 bg-black/75 backdrop-blur-sm text-white text-xs rounded-lg p-3 space-y-1.5 shadow-lg" data-testid="map-legend">
          <p className="font-semibold text-[10px] uppercase tracking-wider text-white/70">Legend</p>
          <div className="flex items-center gap-2"><span className="w-4 h-1 border-t-2 border-dashed border-red-400" /> Field Boundary</div>
          {showCutAreas && <div className="flex items-center gap-2"><span className="w-4 h-3 rounded-sm bg-purple-500 opacity-60" /> Cut Areas</div>}
          {showActivities && <div className="flex items-center gap-2"><span className="w-4 h-4 rounded-full bg-blue-500 flex items-center justify-center text-[8px]">A</span> Activities</div>}
        </div>

        {/* Field summary cards */}
        {data?.fields?.length > 0 && (
          <div className="hidden md:block absolute top-4 left-4 max-h-[calc(100%-100px)] overflow-y-auto space-y-2 w-[200px]" data-testid="field-cards">
            {data.fields.filter(f => f.coordinates?.length >= 3).map((field) => (
              <Card key={field.id} className={`cursor-pointer hover:shadow-md transition-shadow ${selectedField?.id === field.id ? 'ring-2 ring-purple-500' : ''}`}
                onClick={() => {
                  setSelectedField(field);
                  const center = getFieldCenter(field);
                  if (center) setViewState(prev => ({ ...prev, longitude: center[0], latitude: center[1], zoom: 18 }));
                }}
                data-testid={`field-card-${field.id}`}>
                <CardContent className="p-2.5">
                  <p className="font-semibold text-xs truncate">{field.name}</p>
                  <div className="flex items-center justify-between text-[10px] text-muted-foreground mt-1">
                    <span>{field.area_hectares || '—'} ha</span>
                    <span className="text-purple-600 font-medium">{field.total_harvested_sqm?.toLocaleString() || 0} sqm</span>
                  </div>
                  {field.cut_polygons?.length > 0 && (
                    <div className="flex items-center gap-1 mt-1">
                      <Scissors className="h-3 w-3 text-purple-500" />
                      <span className="text-[10px] text-purple-600">{field.cut_polygons.length} areas</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
